import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';

import downloadIcon from 'core/assets/svg/download.svg';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import { useToast } from 'core/components/toast';

const REFRESH_TASK_OUTPUT_URL = gql`
  mutation refreshTaskOutputUrl($taskRunId: String!, $output: String!) {
    toolsRefreshTaskOutputUrl(taskRunId: $taskRunId, output: $output) {
      url
      output
    }
  }
`;

const DownloadButton = ({ path, taskRunId, output, label }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const [refreshUrl] = useMutation(REFRESH_TASK_OUTPUT_URL, {
    onError: (error) => {
      toast.notify({
        type: 'error',
        message: `Failed to generate download URL: ${error.message}`
      });
      setLoading(false);
    }
  });

  const handleDownload = async () => {
    if (taskRunId && output) {
      setLoading(true);
      try {
        const { data } = await refreshUrl({
          variables: { taskRunId, output }
        });
        if (data?.toolsRefreshTaskOutputUrl?.url) {
          window.open(data.toolsRefreshTaskOutputUrl.url);
        }
      } catch (error) {
        console.error('Error refreshing URL:', error);
      } finally {
        setLoading(false);
      }
    } else {
      window.open(path);
    }
  };

  return (
    <Button color="secondary" variant="text" onClick={handleDownload} disabled={loading}>
      <img src={downloadIcon} alt="download" />
      <Label variant="body3">{loading ? 'Loading...' : (label ?? 'Download file')}</Label>
    </Button>
  );
};

DownloadButton.propTypes = {
  path: PropTypes.string,
  taskRunId: PropTypes.string,
  output: PropTypes.string,
  /** Optional descriptive text — the file's description becomes the clickable element. */
  label: PropTypes.string
};

DownloadButton.defaultProps = {
  path: null,
  taskRunId: null,
  output: null,
  label: null
};

export default DownloadButton;
